<template>
  <section class="border-b border-dashed border-[#979797] px-12 pt-6 pb-4 flex flex-col gap-y-5">
    <div v-if="isEditLocked" class="edit-lock-warning px-6">
      <img src="@/assets/encounterNotes/warning.svg" alt="lock" class="w-4 h-4" />
      <span>Another provider is editing this encounter note. Editing is temporarily disabled.</span>
    </div>

    <div class="flex items-center justify-center gap-3">
      <!-- Editable title (draft mode) -->
      <div
        v-if="isDraft"
        class="flex items-center gap-x-2.5"
        :class="isEditLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'"
        @click="startEditingTitle"
      >
        <h3
          v-if="!isEditingTitle"
          class="text-[22px] leading-6 text-[#333]"
        >
          {{ localTitle }}
        </h3>
        <input
          v-else
          ref="titleInputRef"
          v-model="editedTitle"
          type="text"
          class="text-[22px] leading-6 font-medium text-[#333] bg-transparent border-none outline-none focus:outline-none p-0 m-0 w-full cursor-text"
          @keydown.enter="saveTitle"
          @blur="saveTitle"
        />
       
        <div class="relative group flex items-center">
          <img
            v-if="!isEditingTitle"
            src="@/assets/encounterNotes/edit.svg"
            alt="edit"
            class="transition-opacity"
          />
          <span
            v-if="!isEditingTitle"
            class="ml-1 text-[14px] text-[#004879] underline opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none"
          >
            Edit
          </span>
           <NoteStatusBadge status="draft" />
        </div>
      </div>
      
      <!-- Read-only title with Signed badge (signed mode) -->
      <div v-else class="flex items-center gap-x-2">
        <h3 class="text-[22px] leading-6 font-medium text-[#333]">
          {{ localTitle }}
        </h3>
        <NoteStatusBadge status="signed" class="!w-[60px] !min-w-[60px] !px-0 shrink-0" />
      </div>
    </div>

    <div class="flex items-center justify-between gap-4 flex-wrap">
      <div class="flex items-center gap-8 flex-wrap">
        <div class="flex items-center gap-3">
          <label class="text-sm font-medium text-[#4a4a4a]">Appointment Type:</label>
          <!-- Editable dropdown (draft mode) -->
          <DropdownQuestion
            v-if="isDraft"
            v-model="localAppointmentType"
            :options="appointmentTypeOptions"
            placeholder="Select appointment type"
            class="min-w-[210px]"
            :disabled="isEditLocked"
          />
          <!-- Read-only text (signed mode) -->
          <span v-else class="text-sm text-[#4a4a4a]">{{ localAppointmentType }}</span>
        </div>

        <div v-if="isDraft || date" class="flex items-center gap-3">
          <label class="text-sm font-medium text-[#4a4a4a]">Date:</label>
          <!-- Editable date picker (draft mode) -->
          <el-date-picker
            v-if="isDraft"
            v-model="selectedDate"
            type="date"
            placeholder="Select date"
            format="dddd, MMMM D"
            value-format="YYYY-MM-DD"
            :disabled-date="disabledDate"
            :disabled="isEditLocked"
            class="min-w-[210px]"
            size="default"
            :clearable="false"
            @change="handleDateChange"
          />
          <!-- Read-only text (signed mode) -->
          <span v-else class="text-sm text-[#4a4a4a]">{{ formattedDate }}</span>
        </div>
      </div>

      <!-- Draft mode buttons -->
      <div v-if="isDraft" class="flex items-center gap-3">
        <NewButton
          type="round"
          colorType="red-stroke"
          :disabled="isEditLocked"
          @click="showDeleteModal = true"
        >
          Delete
        </NewButton>
        <NewButton
          type="round"
          colorType="dark-blue"
          :disabled="isEditLocked"
          @click="$emit('complete')"
        >
          Complete Encounter Note
        </NewButton>
      </div>
      
      <!-- Signed mode button -->
      <div v-else class="flex items-center">
        <NewButton
          type="round"
          colorType="dark-blue"
          :loading="isDownloading"
          :disabled="isDownloading"
          @click="handleDownload"
        >
          Download Encounter Note
        </NewButton>
      </div>
    </div>

    <DeleteEncounterModal
      v-model="showDeleteModal"
      :note-id="noteId"
      @deleted="handleDeleted"
      @close="showDeleteModal = false"
    />
  </section>
</template>

<script setup>
import { ref, nextTick, watch, computed } from "vue";
import moment from "moment";
import NewButton from "@/NewLibrary/Button.vue";
import DropdownQuestion from "@/views/setting/my-practice/components/DropdownQuestion.vue";
import DeleteEncounterModal from "./DeleteEncounterModal.vue";
import NoteStatusBadge from "./NoteStatusBadge.vue";
import { meetingTypeOptions } from "@/views/my-calendar/mock-data";

const props = defineProps({
  title: { type: String, default: "" },
  date: { type: String, default: "" },
  appointmentType: { type: String, default: "Initial Consultation" },
  noteId: { type: String, default: "" },
  isDraft: { type: Boolean, default: true },
  isEditLocked: { type: Boolean, default: false },
  downloadHandler: { type: Function, default: null },
});

const emit = defineEmits(["deleted", "complete", "update:title", "update:date", "update:appointmentType"]);

// Formatted date for read-only display
const formattedDate = computed(() => {
  if (!props.date) return "";
  return moment(props.date).format("dddd, MMMM D");
});

const localAppointmentType = ref(props.appointmentType);
const appointmentTypeOptions = meetingTypeOptions;

watch(() => props.appointmentType, (newVal) => {
  localAppointmentType.value = newVal;
});

watch(localAppointmentType, (newVal) => {
  emit("update:appointmentType", newVal);
});

const selectedDate = ref(props.date || moment().format("YYYY-MM-DD"));

// Disable dates after today (only allow today and past dates)
const disabledDate = (time) => {
  return moment(time).isAfter(moment(), 'day');
};

const handleDateChange = (date) => {
  if (date) {
    selectedDate.value = date;
    // Emit ISO date string (YYYY-MM-DD)
    emit("update:date", date);
  }
};

// Sync selectedDate with date prop changes
watch(
  () => props.date,
  (newDate) => {
    if (newDate) {
      selectedDate.value = newDate;
    }
  }
);

const isEditingTitle = ref(false);
const localTitle = ref(props.title);
const editedTitle = ref("");
const titleInputRef = ref(null);
const showDeleteModal = ref(false);
const isDownloading = ref(false);

const handleDownload = async () => {
  if (isDownloading.value || !props.downloadHandler) return;
  isDownloading.value = true;
  try {
    await props.downloadHandler();
  } finally {
    isDownloading.value = false;
  }
};

const handleDeleted = () => {
  emit("deleted");
};

// Sync localTitle with prop changes
watch(
  () => props.title,
  (newTitle) => {
    localTitle.value = newTitle;
  }
);

const startEditingTitle = () => {
  if (!props.isDraft || props.isEditLocked) return;
  editedTitle.value = localTitle.value;
  isEditingTitle.value = true;
  nextTick(() => {
    if (titleInputRef.value) {
      titleInputRef.value.focus();
      titleInputRef.value.select();
    }
  });
};

const saveTitle = () => {
  if (editedTitle.value.trim()) {
    localTitle.value = editedTitle.value.trim();
    emit("update:title", localTitle.value);
  }
  // Keep old title if input is empty (localTitle keeps the old value)
  isEditingTitle.value = false;
};
</script>

<style scoped>
.edit-lock-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: #FEF9E9;
  color: #333;
  font-size: 14px;
}

/* Use default Element Plus DatePicker styles */
:deep(.el-date-editor) {
  width: 100%;
  min-width: 210px;
}

/* Restore default Element Plus border (global CSS removes box-shadow) */
:deep(.el-date-editor .el-input__wrapper) {
  border: 1px solid var(--el-input-border-color, #C4C4C4);
}

:deep(.el-date-editor .el-input__wrapper:hover) {
  border-color: var(--el-input-hover-border-color, #C4C4C4);
}

:deep(.el-date-editor .el-input__wrapper.is-focus) {
  border-color: var(--el-input-focus-border-color, #409EFF);
}
</style>
